import React, { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import { OrgChartRow, getAllReportsEmails, isManagerEmail } from '../../../support/orgScope';
import LeaderTileStrip from '../LeaderTileStrip';
import WorkshopsTeamEventsTable from './WorkshopsTeamEventsTable';
import WorkshopsTeamTttProctorsTable from './WorkshopsTeamTttProctorsTable';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';

import { useFetchDashboardPlacements, useFetchDashboardTileLinks } from '../../../hooks/toolbox-hooks';
import type { DashboardPlacement, DashboardTileLink } from '../../../types/dashboard';
import { TileRuntimeContext } from '../../../support/tileRuntimeContext';

interface Props {
    orgRows: OrgChartRow[];
    managerEmail: string;
    onSelectPerson: (email: string) => void;
}

const COMPONENT_KEY_WORKSHOPS_TEAM = 'Leader.WorkshopsTeamSummaryPage';
const REGION_KEY_DASHBOARD = 'dashboard';

export default function WorkshopsTeamSummaryPage({ orgRows, managerEmail, onSelectPerson }: Props) {
    const [isDashboardOpen, setIsDashboardOpen] = useState(true);
    const [isEventsOpen, setIsEventsOpen] = useState(true);
    const [isProctorsOpen, setIsProctorsOpen] = useState(true);

    const manager = String(managerEmail || '').toLowerCase();
    const isMgr = useMemo(() => isManagerEmail(orgRows, manager), [orgRows, manager]);

    const teamEmails = useMemo(() => {
        const reports = getAllReportsEmails(orgRows, manager);
        const all = [manager, ...reports].filter(Boolean);
        return Array.from(new Set(all));
    }, [orgRows, manager]);

    // NEW: Build runtime context for workshops team view
    const runtimeContext: TileRuntimeContext = useMemo(() => {
        return {
            // Primary identifiers
            email: manager,
            managerEmail: manager,

            // Domain context - WORKSHOPS
            domain: 'workshops',

            // Team scope - includes manager + all reports
            teamScope: teamEmails,
            teamEmailsList: teamEmails.join(','),
            teamSize: teamEmails.length,

            // Mode indicators
            viewMode: 'team',
            isTeamView: true,
            isIndividualView: false,

            // Data source
            dataSource: 'workshops',
        };
    }, [manager, teamEmails]);

    // Fetch placement for this component/region
    const {
        data: placements,
        isLoading: isLoadingPlacements,
        error: placementsError,
    } = useFetchDashboardPlacements(
      { component_key: COMPONENT_KEY_WORKSHOPS_TEAM, region_key: REGION_KEY_DASHBOARD, enabled: true },
      { placeholderData: keepPreviousData, refetchOnWindowFocus: false },
    );

    const placement: DashboardPlacement | null = useMemo(() => {
        const arr = Array.isArray(placements) ? placements : [];
        return arr.length ? arr[0] : null;
    }, [placements]);

    // Fetch tile links (ordered) for that placement
    const {
        data: tileLinks,
        isLoading: isLoadingLinks,
        error: linksError,
    } = useFetchDashboardTileLinks(
      placement?.id ? { placement: placement.id, enabled: true, order_by: 'order' } : undefined,
      { enabled: !!placement?.id, placeholderData: keepPreviousData, refetchOnWindowFocus: false },
    );

    if (!isMgr) {
        return <div className="hbr-css__text-secondary-sm">Selected user is not a manager.</div>;
    }

    if (placementsError) {
        return <div style={{ padding: 12, color: 'red' }}>{(placementsError as Error).message}</div>;
    }
    if (linksError) {
        return <div style={{ padding: 12, color: 'red' }}>{(linksError as Error).message}</div>;
    }

    // Layout: grid with colSpan support
    const columns = 2;

    return (
      <div className="flex flex-col gap-4">
          {/* Workshops Team Dashboard Accordion */}
          <StbAccordion
            variant="minimal"
            label="Workshops Team Dashboard"
            isOpen={isDashboardOpen}
            toggleIsOpen={setIsDashboardOpen}
          >
              <div style={{ height: 400, minHeight: 0, padding: 12 }} className="flex flex-col gap-3">
                  {isLoadingPlacements || isLoadingLinks ? (
                    <HbrSpinner>Loading dashboard…</HbrSpinner>
                  ) : !placement || !tileLinks || (Array.isArray(tileLinks) && tileLinks.length === 0) ? (
                    <div className="hbr-css__text-secondary-sm">
                        No dashboard tiles configured for {COMPONENT_KEY_WORKSHOPS_TEAM}/{REGION_KEY_DASHBOARD}.
                    </div>
                  ) : (
                    <div
                      className="grid gap-3"
                      style={{
                          height: '100%',
                          minHeight: 0,
                          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                      }}
                    >
                        {(Array.isArray(tileLinks) ? (tileLinks as DashboardTileLink[]) : [])
                          .slice()
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id)
                          .map((link) => {
                              const tileName = link?.tile?.name;
                              if (!tileName) return null;

                              const colSpan = Number((link.options as any)?.colSpan ?? 1);
                              const minHeight = (link.options as any)?.minHeight ?? 0;

                              return (
                                <div
                                  key={`dashboard-tile-${link.id}`}
                                  className="min-w-0 flex flex-col"
                                  style={{
                                      minHeight,
                                      height: '100%',
                                      gridColumn: `span ${Math.max(1, Math.min(columns, colSpan))}`,
                                  }}
                                >
                                    <LeaderTileStrip
                                      tileNames={[tileName]}
                                      runtimeContext={runtimeContext}
                                      className="h-full min-w-0"
                                    />
                                </div>
                              );
                          })}
                    </div>
                  )}
              </div>
          </StbAccordion>

          {/* Workshops Events Accordion */}
          <StbAccordion
            variant="minimal"
            label="Workshops (Team)"
            isOpen={isEventsOpen}
            toggleIsOpen={setIsEventsOpen}
          >
              <div style={{ height: 600, minHeight: 0, padding: 12 }}>
                  <WorkshopsTeamEventsTable teamEmails={teamEmails} onSelectPerson={onSelectPerson} />
              </div>
          </StbAccordion>

          {/* Train the Trainer Accordion */}
          <StbAccordion
            variant="minimal"
            label="Train the Trainer – Proctor Readiness"
            isOpen={isProctorsOpen}
            toggleIsOpen={setIsProctorsOpen}
          >
              <div style={{ height: 600, minHeight: 0, padding: 12 }}>
                  <WorkshopsTeamTttProctorsTable teamEmails={teamEmails} onSelectPerson={onSelectPerson} />
              </div>
          </StbAccordion>
      </div>
    );
}